import { useState, useEffect, useCallback, useRef } from "react";
import { Button, Table, Spin, Alert, message, Tag, Select, Row, Col, Input, Modal, Space } from "antd";
import { FileExcelOutlined, ReloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { getIncomeVerificationReport, getDistrict } from "../api/User";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const IncomeVerificationReportCEO = ({ dateFilter }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Main Page pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [mainLevel, setMainLevel] = useState("district");

  // User details & District locking
  const [roleId, setRoleId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isDistrictLocked, setIsDistrictLocked] = useState(false);
  const [districtCode, setDistrictCode] = useState(null);
  const [districts, setDistricts] = useState([]);

  // Drill-down path tracking for Modal headers
  const [selectedPath, setSelectedPath] = useState({
    district_code: null,
    district_name: "",
    block_code: null,
    block_name: "",
    gp_code: null,
    gp_name: "",
    village_code: null,
    village_name: "",
  });

  // -------- Block Modal States --------
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockModalTitle, setBlockModalTitle] = useState("");
  const [blockData, setBlockData] = useState([]);
  const [blockLoading, setBlockLoading] = useState(false);
  const [blockCurrentPage, setBlockCurrentPage] = useState(1);
  const [blockPageSize, setBlockPageSize] = useState(10);

  // -------- GP Modal States --------
  const [gpModalOpen, setGpModalOpen] = useState(false);
  const [gpModalTitle, setGpModalTitle] = useState("");
  const [gpData, setGpData] = useState([]);
  const [gpLoading, setGpLoading] = useState(false);
  const [gpCurrentPage, setGpCurrentPage] = useState(1);
  const [gpPageSize, setGpPageSize] = useState(10);

  // -------- Village Modal States --------
  const [villageModalOpen, setVillageModalOpen] = useState(false);
  const [villageModalTitle, setVillageModalTitle] = useState("");
  const [villageData, setVillageData] = useState([]);
  const [villageLoading, setVillageLoading] = useState(false);
  const [villageCurrentPage, setVillageCurrentPage] = useState(1);
  const [villagePageSize, setVillagePageSize] = useState(10);



  // ================= CLICK STYLE =================
  const clickStyle = {
    color: "#1677ff",
    cursor: "pointer",
    textDecoration: "underline",
    fontWeight: 500,
  };

  // ================= SORTING HELPERS =================
  const numberSorter = (a, b, key) => {
    const valA = Number(a[key]) || 0;
    const valB = Number(b[key]) || 0;
    return valA - valB;
  };

  const stringSorter = (a, b, key) => {
    const valA = (a[key] || '').toString().toLowerCase();
    const valB = (b[key] || '').toString().toLowerCase();
    return valA.localeCompare(valB);
  };

  // ================= GRAND TOTALS CALCULATIONS =================
  const mainTotals = {
    block_count: data.reduce((s, i) => s + Number(i.block_count || 0), 0),
    gp_count: data.reduce((s, i) => s + Number(i.gp_count || 0), 0),
    total_families: data.reduce((s, i) => s + Number(i.total_families || 0), 0),
    total_members: data.reduce((s, i) => s + Number(i.total_members || 0), 0),
    income_verified: data.reduce((s, i) => s + Number(i.income_verified || 0), 0),
    income_not_verified: data.reduce((s, i) => s + Number(i.income_not_verified || 0), 0),
    income_pending: data.reduce((s, i) => s + Number(i.income_pending || 0), 0),
  };

  const blockTotals = {
    gp_count: blockData.reduce((s, i) => s + Number(i.gp_count || 0), 0),
    total_families: blockData.reduce((s, i) => s + Number(i.total_families || 0), 0),
    total_members: blockData.reduce((s, i) => s + Number(i.total_members || 0), 0),
    income_verified: blockData.reduce((s, i) => s + Number(i.income_verified || 0), 0),
    income_not_verified: blockData.reduce((s, i) => s + Number(i.income_not_verified || 0), 0),
    income_pending: blockData.reduce((s, i) => s + Number(i.income_pending || 0), 0),
  };

  const gpTotals = {
    total_families: gpData.reduce((s, i) => s + Number(i.total_families || 0), 0),
    total_members: gpData.reduce((s, i) => s + Number(i.total_members || 0), 0),
    income_verified: gpData.reduce((s, i) => s + Number(i.income_verified || 0), 0),
    income_not_verified: gpData.reduce((s, i) => s + Number(i.income_not_verified || 0), 0),
    income_pending: gpData.reduce((s, i) => s + Number(i.income_pending || 0), 0),
  };

  const villageTotals = {
    total_families: villageData.reduce((s, i) => s + Number(i.total_families || 0), 0),
    total_members: villageData.reduce((s, i) => s + Number(i.total_members || 0), 0),
    income_verified: villageData.reduce((s, i) => s + Number(i.income_verified || 0), 0),
    income_not_verified: villageData.reduce((s, i) => s + Number(i.income_not_verified || 0), 0),
    income_pending: villageData.reduce((s, i) => s + Number(i.income_pending || 0), 0),
  };

  // ================= DATA FETCH LOGIC =================

  // Fetch Summary data for the main page
  const fetchMainData = async (distCode, currentLevel = mainLevel) => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("token");
      const payload = {
        level: currentLevel,
        page: 1,
        limit: 5000,
        district_code: distCode || undefined,
        user_id: sessionStorage.getItem("user_id"),
        role_id: sessionStorage.getItem("role_id"),
        period: dateFilter || undefined,
      };

      const res = await getIncomeVerificationReport(token, payload);
      if (res.success) {
        const formatted = (res.data || []).map((row, idx) => ({
          ...row,
          key: row.district_code || row.block_code || row.panchayat_code || idx,
          sr_no: idx + 1,
          gp_name: row.panchayat_name || row.gp_name,
          gp_code: row.panchayat_code || row.gp_code,
        }));
        setData(formatted);
      } else {
        setError(res.message || "Failed to load report data");
        setData([]);
      }
    } catch (err) {
      console.error("fetchMainData error:", err);
      setError(err.message || "Failed to load report data");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial mount details retrieval & lock-detection
  useEffect(() => {
    const role = sessionStorage.getItem("role_id");
    const uId = sessionStorage.getItem("user_id");
    setRoleId(role);
    setUserId(uId);

    const initReport = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem("token");
        const distResult = await getDistrict(token, uId, role);

        let initialLevel = "district";
        if (role === "7") {
          initialLevel = "block";
        } else if (role === "2") {
          initialLevel = "gp";
        }
        setMainLevel(initialLevel);

        if (distResult.status === "success" && distResult.data && distResult.data.length > 0) {
          const list = distResult.data;
          setDistricts(list);

          if (role === "8" || role === "7" || role === "2") {
            const lockedDist = list[0].district_code;
            const lockedName = list[0].district;
            setDistrictCode(lockedDist);
            setIsDistrictLocked(true);
            setSelectedPath((prev) => ({
              ...prev,
              district_code: lockedDist,
              district_name: lockedName,
            }));

            // Display initial summary on main page
            await fetchMainData(lockedDist, initialLevel);
          } else {
            // Otherwise, show all districts
            await fetchMainData(null, initialLevel);
          }
        } else {
          if (role === "8" || role === "7" || role === "2") {
            setError("No geography is assigned to your account. Please contact the administrator.");
            setData([]);
          } else {
            setMainLevel("district");
            await fetchMainData(null, "district");
          }
        }
      } catch (err) {
        console.error("initReport Error:", err);
        setError("Failed to fetch geographic profiles");
      } finally {
        setLoading(false);
      }
    };

    initReport();
  }, []);

  // Re-fetch when dateFilter changes
  useEffect(() => {
    if (roleId) {
      fetchMainData(districtCode, mainLevel);
    }
  }, [dateFilter]);

  // Refresh helper for main page
  const refreshMainData = () => {
    fetchMainData(districtCode, mainLevel);
  };

  // ================= MODAL OPEN & FETCH DIRECTIVES =================

  // 1. Open Block Modal
  const openBlockModal = async (record) => {
    setSelectedPath((prev) => ({
      ...prev,
      district_code: record.district_code,
      district_name: record.district_name,
    }));
    setBlockModalTitle(`Block-wise Income Verification Report: ${record.district_name}`);
    setBlockModalOpen(true);
    setBlockData([]);
    setBlockLoading(true);

    try {
      const token = sessionStorage.getItem("token");
      const payload = {
        level: "block",
        district_code: record.district_code,
        page: 1,
        limit: 5000,
        user_id: sessionStorage.getItem("user_id"),
        role_id: sessionStorage.getItem("role_id"),
        period: dateFilter || undefined,
      };
      const res = await getIncomeVerificationReport(token, payload);
      if (res.success) {
        const formatted = (res.data || []).map((row, idx) => ({
          ...row,
          key: row.block_code,
          sr_no: idx + 1,
        }));
        setBlockData(formatted);
      } else {
        message.error(res.message || "Failed to load blocks");
      }
    } catch (err) {
      console.error("openBlockModal error:", err);
      message.error(err.message || "Failed to load blocks");
    } finally {
      setBlockLoading(false);
    }
  };

  // 2. Open GP Modal
  const openGpModal = async (record) => {
    setSelectedPath((prev) => ({
      ...prev,
      block_code: record.block_code || null,
      block_name: record.block_name || "",
      district_code: record.district_code || prev.district_code,
      district_name: record.district_name || prev.district_name,
    }));

    const distName = record.district_name || selectedPath.district_name;
    const distCode = record.district_code || selectedPath.district_code;
    const blockName = record.block_name || "";

    const titleText = blockName
      ? `Gram Panchayat-wise Income Verification Report: ${blockName} (${distName})`
      : `Gram Panchayat-wise Income Verification Report: District ${distName}`;

    setGpModalTitle(titleText);
    setGpModalOpen(true);
    setGpData([]);
    setGpLoading(true);

    try {
      const token = sessionStorage.getItem("token");
      const payload = {
        level: "gp",
        district_code: distCode,
        block_code: record.block_code || undefined,
        page: 1,
        limit: 5000,
        user_id: sessionStorage.getItem("user_id"),
        role_id: sessionStorage.getItem("role_id"),
        period: dateFilter || undefined,
      };
      const res = await getIncomeVerificationReport(token, payload);
      if (res.success) {
        const formatted = (res.data || []).map((row, idx) => ({
          ...row,
          key: row.panchayat_code,
          sr_no: idx + 1,
          gp_name: row.panchayat_name,
          gp_code: row.panchayat_code,
        }));
        setGpData(formatted);
      } else {
        message.error(res.message || "Failed to load GPs");
      }
    } catch (err) {
      console.error("openGpModal error:", err);
      message.error(err.message || "Failed to load GPs");
    } finally {
      setGpLoading(false);
    }
  };

  // 3. Open Village Modal
  const openVillageModal = async (record) => {
    setSelectedPath((prev) => ({
      ...prev,
      gp_code: record.gp_code || record.panchayat_code,
      gp_name: record.gp_name || record.panchayat_name,
      block_code: record.block_code || prev.block_code,
      block_name: record.block_name || prev.block_name,
    }));

    const distName = record.district_name || selectedPath.district_name;
    const distCode = record.district_code || selectedPath.district_code;
    const blockName = record.block_name || selectedPath.block_name;
    const blockCode = record.block_code || selectedPath.block_code;
    const gpCode = record.gp_code || record.panchayat_code;
    const gpName = record.gp_name || record.panchayat_name;

    setVillageModalTitle(`Village-wise Income Verification Report: ${gpName} (${blockName}, ${distName})`);
    setVillageModalOpen(true);
    setVillageData([]);
    setVillageLoading(true);

    try {
      const token = sessionStorage.getItem("token");
      const payload = {
        level: "village",
        district_code: distCode,
        block_code: blockCode,
        gram_panchayat_code: gpCode,
        page: 1,
        limit: 5000,
        user_id: sessionStorage.getItem("user_id"),
        role_id: sessionStorage.getItem("role_id"),
        period: dateFilter || undefined,
      };
      const res = await getIncomeVerificationReport(token, payload);
      if (res.success) {
        const formatted = (res.data || []).map((row, idx) => ({
          ...row,
          key: row.village_code,
          sr_no: idx + 1,
          gp_name: row.panchayat_name || gpName,
          gp_code: row.panchayat_code || gpCode,
        }));
        setVillageData(formatted);
      } else {
        message.error(res.message || "Failed to load villages");
      }
    } catch (err) {
      console.error("openVillageModal error:", err);
      message.error(err.message || "Failed to load villages");
    } finally {
      setVillageLoading(false);
    }
  };



  // ================= GENERALIZED EXPORTS (EXCEL & PDF) =================

  // Excel Summary Exporter
  const exportToExcel = (dataList, totals, levelName, typeName) => {
    if (!dataList || dataList.length === 0) {
      message.warning("No data to export");
      return;
    }
    try {
      const excelRows = dataList.map((row, idx) => {
        const rowData = { "Sr. No.": idx + 1 };
        if (typeName === "district") {
          rowData["District"] = row.district_name || "-";
          rowData["No. of Blocks"] = row.block_count || 0;
          rowData["No. of Gram Panchayats"] = row.gp_count || 0;
        } else if (typeName === "block") {
          rowData["District"] = row.district_name || "-";
          rowData["Block"] = row.block_name || "-";
          rowData["No. of Gram Panchayats"] = row.gp_count || 0;
        } else if (typeName === "gp") {
          rowData["District"] = row.district_name || "-";
          rowData["Block"] = row.block_name || "-";
          rowData["Gram Panchayat"] = row.gp_name || "-";
        } else if (typeName === "village") {
          rowData["District"] = row.district_name || "-";
          rowData["Block"] = row.block_name || "-";
          rowData["Gram Panchayat"] = row.gp_name || "-";
          rowData["Village"] = row.village_name || "-";
        }
        rowData["Total No. of Families"] = row.total_families || 0;
        rowData["Total No. of Members"] = row.total_members || 0;
        rowData["Income Verified (Yes)"] = row.income_verified || 0;
        rowData["Not Verified (No)"] = row.income_not_verified || 0;
        rowData["Pending"] = row.income_pending || 0;
        return rowData;
      });

      // Insert Grand Totals bottom row
      const totalRow = { "Sr. No.": "" };
      if (typeName === "district") {
        totalRow["District"] = "GRAND TOTAL";
        totalRow["No. of Blocks"] = totals.block_count;
        totalRow["No. of Gram Panchayats"] = totals.gp_count;
      } else if (typeName === "block") {
        totalRow["District"] = "GRAND TOTAL";
        totalRow["Block"] = "";
        totalRow["No. of Gram Panchayats"] = totals.gp_count;
      } else if (typeName === "gp") {
        totalRow["District"] = "GRAND TOTAL";
        totalRow["Block"] = "";
        totalRow["Gram Panchayat"] = "";
      } else if (typeName === "village") {
        totalRow["District"] = "GRAND TOTAL";
        totalRow["Block"] = "";
        totalRow["Gram Panchayat"] = "";
        totalRow["Village"] = "";
      }
      totalRow["Total No. of Families"] = totals.total_families;
      totalRow["Total No. of Members"] = totals.total_members;
      totalRow["Income Verified (Yes)"] = totals.income_verified;
      totalRow["Not Verified (No)"] = totals.income_not_verified;
      totalRow["Pending"] = totals.income_pending;

      excelRows.push(totalRow);

      const ws = XLSX.utils.json_to_sheet(excelRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${levelName} Summary`);
      XLSX.writeFile(wb, `Income_Verification_${levelName}_Report_${Date.now()}.xlsx`);
      message.success("Excel exported successfully");
    } catch (err) {
      console.error("Excel Export error:", err);
      message.error("Failed to export Excel");
    }
  };

  // PDF Summary Exporter
  const exportToPDF = (dataList, totals, levelName, typeName) => {
    if (!dataList || dataList.length === 0) {
      message.warning("No data to export");
      return;
    }
    try {
      const doc = new jsPDF("landscape");
      doc.setFontSize(16);
      doc.setTextColor(40, 40, 40);
      doc.text(`Income Verification Report - ${levelName} Summary`, 148, 15, { align: "center" });

      doc.setFontSize(9);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 148, 20, { align: "center" });

      let headers = [];
      if (typeName === "district") {
        headers = ["Sr. No.", "District", "Blocks", "GPs", "Families", "Members", "Income Ver", "Not Ver", "Pen"];
      } else if (typeName === "block") {
        headers = ["Sr. No.", "District", "Block", "GPs", "Families", "Members", "Income Ver", "Not Ver", "Pen"];
      } else if (typeName === "gp") {
        headers = ["Sr. No.", "District", "Block", "GP", "Families", "Members", "Income Ver", "Not Ver", "Pen"];
      } else if (typeName === "village") {
        headers = ["Sr. No.", "District", "Block", "GP", "Village", "Families", "Members", "Income Ver", "Not Ver", "Pen"];
      }

      const body = dataList.map((row, idx) => {
        const baseRow = [idx + 1];
        if (typeName === "district") {
          baseRow.push(row.district_name || "-", row.block_count || 0, row.gp_count || 0);
        } else if (typeName === "block") {
          baseRow.push(row.district_name || "-", row.block_name || "-", row.gp_count || 0);
        } else if (typeName === "gp") {
          baseRow.push(row.district_name || "-", row.block_name || "-", row.gp_name || "-");
        } else if (typeName === "village") {
          baseRow.push(row.district_name || "-", row.block_name || "-", row.gp_name || "-", row.village_name || "-");
        }
        baseRow.push(
          row.total_families || 0,
          row.total_members || 0,
          row.income_verified || 0,
          row.income_not_verified || 0,
          row.income_pending || 0
        );
        return baseRow;
      });

      // Insert Grand Totals bottom row
      const totalRow = ["GRAND TOTAL"];
      if (typeName === "district") {
        totalRow.push("", totals.block_count, totals.gp_count);
      } else if (typeName === "block") {
        totalRow.push("", "", totals.gp_count);
      } else if (typeName === "gp") {
        totalRow.push("", "", "");
      } else if (typeName === "village") {
        totalRow.push("", "", "", "");
      }
      totalRow.push(
        totals.total_families,
        totals.total_members,
        totals.income_verified,
        totals.income_not_verified,
        totals.income_pending
      );
      body.push(totalRow);

      autoTable(doc, {
        head: [headers],
        body: body,
        startY: 25,
        theme: "grid",
        headStyles: {
          fillColor: [22, 119, 255],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 8,
        },
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          halign: "center",
        },
        didParseCell: function (dataInfo) {
          if (dataInfo.row.index === body.length - 1) {
            dataInfo.cell.styles.fillColor = [240, 249, 255];
            dataInfo.cell.styles.fontStyle = "bold";
            dataInfo.cell.styles.textColor = [0, 0, 0];
          }
        },
      });

      doc.save(`Income_Verification_${levelName}_Report_${Date.now()}.pdf`);
      message.success("PDF exported successfully");
    } catch (err) {
      console.error("PDF Export error:", err);
      message.error("Failed to export PDF");
    }
  };



  // ================= MAIN COLUMN SETUP =================
  const getMainColumns = () => {
    if (mainLevel === "block") {
      return [
        {
          title: "Sr. No.",
          dataIndex: "sr_no",
          width: 70,
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'sr_no'),
        },
        {
          title: "District Name",
          dataIndex: "district_name",
          align: "center",
          sorter: (a, b) => stringSorter(a, b, 'district_name'),
        },
        {
          title: "Block Name",
          dataIndex: "block_name",
          align: "center",
          sorter: (a, b) => stringSorter(a, b, 'block_name'),
          render: (text, record) => (
            <span onClick={() => openGpModal(record)} style={clickStyle}>
              {text}
            </span>
          ),
        },
        {
          title: "No. of Gram Panchayats",
          dataIndex: "gp_count",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'gp_count'),
          render: (val, record) => (
            <span onClick={() => openGpModal(record)} style={clickStyle}>
              {val || 0}
            </span>
          ),
        },
        {
          title: "Total No. of Families",
          dataIndex: "total_families",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'total_families'),
        },
        {
          title: "Total No. of Members",
          dataIndex: "total_members",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'total_members'),
        },
        {
          title: "Income Verified (Yes)",
          dataIndex: "income_verified",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'income_verified'),
          render: (val) => <Tag color="green">{val || 0}</Tag>,
        },
        {
          title: "Not Verified (No)",
          dataIndex: "income_not_verified",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'income_not_verified'),
          render: (val) => <Tag color="red">{val || 0}</Tag>,
        },
        {
          title: "Pending",
          dataIndex: "income_pending",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'income_pending'),
          render: (val) => <Tag color="orange">{val || 0}</Tag>,
        },
      ];
    }

    if (mainLevel === "gp") {
      return [
        {
          title: "Sr. No.",
          dataIndex: "sr_no",
          width: 70,
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'sr_no'),
        },
        {
          title: "District Name",
          dataIndex: "district_name",
          align: "center",
          sorter: (a, b) => stringSorter(a, b, 'district_name'),
        },
        {
          title: "Block Name",
          dataIndex: "block_name",
          align: "center",
          sorter: (a, b) => stringSorter(a, b, 'block_name'),
        },
        {
          title: "Gram Panchayat Name",
          dataIndex: "gp_name",
          align: "center",
          sorter: (a, b) => stringSorter(a, b, 'gp_name'),
          render: (text, record) => (
            <span onClick={() => openVillageModal(record)} style={clickStyle}>
              {text}
            </span>
          ),
        },
        {
          title: "Total No. of Families",
          dataIndex: "total_families",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'total_families'),
        },
        {
          title: "Total No. of Members",
          dataIndex: "total_members",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'total_members'),
        },
        {
          title: "Income Verified (Yes)",
          dataIndex: "income_verified",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'income_verified'),
          render: (val) => <Tag color="green">{val || 0}</Tag>,
        },
        {
          title: "Not Verified (No)",
          dataIndex: "income_not_verified",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'income_not_verified'),
          render: (val) => <Tag color="red">{val || 0}</Tag>,
        },
        {
          title: "Pending",
          dataIndex: "income_pending",
          align: "center",
          sorter: (a, b) => numberSorter(a, b, 'income_pending'),
          render: (val) => <Tag color="orange">{val || 0}</Tag>,
        },
      ];
    }

    return [
      {
        title: "Sr. No.",
        dataIndex: "sr_no",
        width: 70,
        align: "center",
        sorter: (a, b) => numberSorter(a, b, 'sr_no'),
      },
      {
        title: "District",
        dataIndex: "district_name",
        align: "center",
        sorter: (a, b) => stringSorter(a, b, 'district_name'),
      },
      {
        title: "No. of Blocks",
        dataIndex: "block_count",
        align: "center",
        sorter: (a, b) => numberSorter(a, b, 'block_count'),
        render: (val, record) => (
          <span onClick={() => openBlockModal(record)} style={clickStyle}>
            {val || 0}
          </span>
        ),
      },
      {
        title: "No. of Gram Panchayats",
        dataIndex: "gp_count",
        align: "center",
        sorter: (a, b) => numberSorter(a, b, 'gp_count'),
        render: (val, record) => (
          <span onClick={() => openGpModal(record)} style={clickStyle}>
            {val || 0}
          </span>
        ),
      },
      {
        title: "Total No. of Families",
        dataIndex: "total_families",
        align: "center",
        sorter: (a, b) => numberSorter(a, b, 'total_families'),
      },
      {
        title: "Total No. of Members",
        dataIndex: "total_members",
        align: "center",
        sorter: (a, b) => numberSorter(a, b, 'total_members'),
      },
      {
        title: "Income Verified (Yes)",
        dataIndex: "income_verified",
        align: "center",
        sorter: (a, b) => numberSorter(a, b, 'income_verified'),
        render: (val) => <Tag color="green">{val || 0}</Tag>,
      },
      {
        title: "Not Verified (No)",
        dataIndex: "income_not_verified",
        align: "center",
        sorter: (a, b) => numberSorter(a, b, 'income_not_verified'),
        render: (val) => <Tag color="red">{val || 0}</Tag>,
      },
      {
        title: "Pending",
        dataIndex: "income_pending",
        align: "center",
        sorter: (a, b) => numberSorter(a, b, 'income_pending'),
        render: (val) => <Tag color="orange">{val || 0}</Tag>,
      },
    ];
  };

  // ================= MODAL COLUMN SETUP =================

  // Block Modal Columns
  const blockColumns = [
    {
      title: "Sr. No.",
      dataIndex: "sr_no",
      width: 70,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'sr_no'),
    },
    {
      title: "District Name",
      dataIndex: "district_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'district_name'),
    },
    {
      title: "Block Name",
      dataIndex: "block_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'block_name'),
      render: (text, record) => (
        <span onClick={() => openGpModal(record)} style={clickStyle}>
          {text}
        </span>
      ),
    },
    {
      title: "No. of Gram Panchayats",
      dataIndex: "gp_count",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'gp_count'),
      render: (val, record) => (
        <span onClick={() => openGpModal(record)} style={clickStyle}>
          {val || 0}
        </span>
      ),
    },
    {
      title: "Total No. of Families",
      dataIndex: "total_families",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'total_families'),
    },
    {
      title: "Total No. of Members",
      dataIndex: "total_members",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'total_members'),
    },
    {
      title: "Income Verified (Yes)",
      dataIndex: "income_verified",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_verified'),
      render: (val) => <Tag color="green">{val || 0}</Tag>,
    },
    {
      title: "Not Verified (No)",
      dataIndex: "income_not_verified",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_not_verified'),
      render: (val) => <Tag color="red">{val || 0}</Tag>,
    },
    {
      title: "Pending",
      dataIndex: "income_pending",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_pending'),
      render: (val) => <Tag color="orange">{val || 0}</Tag>,
    },
  ];

  // GP Modal Columns
  const gpColumns = [
    {
      title: "Sr. No.",
      dataIndex: "sr_no",
      width: 70,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'sr_no'),
    },
    {
      title: "District Name",
      dataIndex: "district_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'district_name'),
    },
    {
      title: "Block Name",
      dataIndex: "block_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'block_name'),
    },
    {
      title: "Gram Panchayat Name",
      dataIndex: "gp_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'gp_name'),
      render: (text, record) => (
        <span onClick={() => openVillageModal(record)} style={clickStyle}>
          {text}
        </span>
      ),
    },
    {
      title: "Total No. of Families",
      dataIndex: "total_families",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'total_families'),
    },
    {
      title: "Total No. of Members",
      dataIndex: "total_members",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'total_members'),
    },
    {
      title: "Income Verified (Yes)",
      dataIndex: "income_verified",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_verified'),
      render: (val) => <Tag color="green">{val || 0}</Tag>,
    },
    {
      title: "Not Verified (No)",
      dataIndex: "income_not_verified",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_not_verified'),
      render: (val) => <Tag color="red">{val || 0}</Tag>,
    },
    {
      title: "Pending",
      dataIndex: "income_pending",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_pending'),
      render: (val) => <Tag color="orange">{val || 0}</Tag>,
    },
  ];

  // Village Modal Columns
  const villageColumns = [
    {
      title: "Sr. No.",
      dataIndex: "sr_no",
      width: 70,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'sr_no'),
    },
    {
      title: "District Name",
      dataIndex: "district_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'district_name'),
    },
    {
      title: "Block Name",
      dataIndex: "block_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'block_name'),
    },
    {
      title: "Gram Panchayat Name",
      dataIndex: "gp_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'gp_name'),
    },
    {
      title: "Village Name",
      dataIndex: "village_name",
      align: "center",
      sorter: (a, b) => stringSorter(a, b, 'village_name'),
    },
    {
      title: "Total No. of Families",
      dataIndex: "total_families",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'total_families'),
    },
    {
      title: "Total No. of Members",
      dataIndex: "total_members",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'total_members'),
    },
    {
      title: "Income Verified (Yes)",
      dataIndex: "income_verified",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_verified'),
      render: (val) => <Tag color="green">{val || 0}</Tag>,
    },
    {
      title: "Not Verified (No)",
      dataIndex: "income_not_verified",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_not_verified'),
      render: (val) => <Tag color="red">{val || 0}</Tag>,
    },
    {
      title: "Pending",
      dataIndex: "income_pending",
      align: "center",
      sorter: (a, b) => numberSorter(a, b, 'income_pending'),
      render: (val) => <Tag color="orange">{val || 0}</Tag>,
    },
  ];

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  return (
    <div style={{ padding: 24, background: "#f0f2f5", minHeight: "100vh" }}>
      <div
        style={{
          background: "#fff",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {/* Main Page Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1f1f1f", margin: 0 }}>
              {roleId === "8"
                ? "Income Verification Report (CEO ZP)"
                : roleId === "7"
                  ? "Income Verification Report (BDPO)"
                  : roleId === "2"
                    ? "Income Verification Report (Gram Sachiv)"
                    : "Income Verification Report"}
            </h2>
            <p style={{ margin: "4px 0 0 0", color: "#666" }}>
              Total Records: <strong>{data.length}</strong>
            </p>
          </div>

          <Space>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => {
                const levelLabel = mainLevel.charAt(0).toUpperCase() + mainLevel.slice(1);
                exportToPDF(data, mainTotals, levelLabel, mainLevel);
              }}
              disabled={loading || data.length === 0}
            >
              Export PDF
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() => {
                const levelLabel = mainLevel.charAt(0).toUpperCase() + mainLevel.slice(1);
                exportToExcel(data, mainTotals, levelLabel, mainLevel);
              }}
              disabled={loading || data.length === 0}
            >
              Export Excel
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refreshMainData}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

        {/* Main Table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <Table
              columns={getMainColumns()}
              dataSource={data}
              bordered
              size="small"
              scroll={{ x: 1800, y: 420 }}
              pagination={{
                current: currentPage,
                pageSize,
                total: data.length,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
              }}
              onChange={handleTableChange}
            />

            {data.length > 0 && (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 14px",
                  background: "#e6f7ff",
                  border: "1px solid #91d5ff",
                  borderRadius: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  fontSize: 12,
                }}
              >
                <span><b>Grand Totals:</b></span>
                {mainLevel === "district" && (
                  <>
                    <span>Blocks: <strong>{mainTotals.block_count.toLocaleString()}</strong></span>
                    <span>GPs: <strong>{mainTotals.gp_count.toLocaleString()}</strong></span>
                  </>
                )}
                {mainLevel === "block" && (
                  <span>GPs: <strong>{mainTotals.gp_count.toLocaleString()}</strong></span>
                )}
                <span>Families: <strong>{mainTotals.total_families.toLocaleString()}</strong></span>
                <span>Members: <strong>{mainTotals.total_members.toLocaleString()}</strong></span>

                <span>Income Verified: <strong>{mainTotals.income_verified.toLocaleString()}</strong></span>
                <span>Not Verified: <strong>{mainTotals.income_not_verified.toLocaleString()}</strong></span>
                <span>Pending: <strong>{mainTotals.income_pending.toLocaleString()}</strong></span>
              </div>
            )}
          </>
        )}

        {/* ==================== BLOCK MODAL ==================== */}
        <Modal
          open={blockModalOpen}
          onCancel={() => setBlockModalOpen(false)}
          width={1400}
          destroyOnClose
          footer={null}
          closable
          style={{ top: 20 }}
          styles={{ body: { padding: "16px 0 0 0", overflow: "hidden" } }}
          title={
            <div style={{ paddingRight: 40 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "16px", fontWeight: "600", color: "#1f1f1f" }}>
                  {blockModalTitle}
                </span>
                <Space>
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={() => exportToPDF(blockData, blockTotals, "Block", "block")}
                    disabled={blockLoading || blockData.length === 0}
                  >
                    Export PDF
                  </Button>
                  <Button
                    type="primary"
                    icon={<FileExcelOutlined />}
                    onClick={() => exportToExcel(blockData, blockTotals, "Block", "block")}
                    disabled={blockLoading || blockData.length === 0}
                  >
                    Export Excel
                  </Button>
                </Space>
              </div>
            </div>
          }
        >
          {blockLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
              <p style={{ marginTop: 10, color: "#666" }}>Loading blocks...</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", height: "calc(80vh - 110px)", overflow: "hidden" }}>
              <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
                <Table
                  columns={blockColumns}
                  dataSource={blockData}
                  bordered
                  size="small"
                  scroll={{ x: 1800 }}
                  pagination={{
                    current: blockCurrentPage,
                    pageSize: blockPageSize,
                    total: blockData.length,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} blocks`,
                  }}
                  onChange={(p) => {
                    setBlockCurrentPage(p.current);
                    setBlockPageSize(p.pageSize);
                  }}
                />
              </div>

              {blockData.length > 0 && (
                <div
                  style={{
                    margin: "12px 16px",
                    padding: "10px 14px",
                    background: "#e6f7ff",
                    border: "1px solid #91d5ff",
                    borderRadius: 6,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    fontSize: 12,
                  }}
                >
                  <span><b>Grand Totals:</b></span>
                  <span>GPs: <strong>{blockTotals.gp_count.toLocaleString()}</strong></span>
                  <span>Families: <strong>{blockTotals.total_families.toLocaleString()}</strong></span>
                  <span>Members: <strong>{blockTotals.total_members.toLocaleString()}</strong></span>
                  <span>Income Verified: <strong>{blockTotals.income_verified.toLocaleString()}</strong></span>
                  <span>Not Verified: <strong>{blockTotals.income_not_verified.toLocaleString()}</strong></span>
                  <span>Pending: <strong>{blockTotals.income_pending.toLocaleString()}</strong></span>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* ==================== GP MODAL ==================== */}
        <Modal
          open={gpModalOpen}
          onCancel={() => setGpModalOpen(false)}
          width={1400}
          destroyOnClose
          footer={null}
          closable
          style={{ top: 20 }}
          styles={{ body: { padding: "16px 0 0 0", overflow: "hidden" } }}
          title={
            <div style={{ paddingRight: 40 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "16px", fontWeight: "600", color: "#1f1f1f" }}>
                  {gpModalTitle}
                </span>
                <Space>
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={() => exportToPDF(gpData, gpTotals, "GP", "gp")}
                    disabled={gpLoading || gpData.length === 0}
                  >
                    Export PDF
                  </Button>
                  <Button
                    type="primary"
                    icon={<FileExcelOutlined />}
                    onClick={() => exportToExcel(gpData, gpTotals, "GP", "gp")}
                    disabled={gpLoading || gpData.length === 0}
                  >
                    Export Excel
                  </Button>
                </Space>
              </div>
            </div>
          }
        >
          {gpLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
              <p style={{ marginTop: 10, color: "#666" }}>Loading Gram Panchayats...</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", height: "calc(80vh - 110px)", overflow: "hidden" }}>
              <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
                <Table
                  columns={gpColumns}
                  dataSource={gpData}
                  bordered
                  size="small"
                  scroll={{ x: 1800 }}
                  pagination={{
                    current: gpCurrentPage,
                    pageSize: gpPageSize,
                    total: gpData.length,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} GPs`,
                  }}
                  onChange={(p) => {
                    setGpCurrentPage(p.current);
                    setGpPageSize(p.pageSize);
                  }}
                />
              </div>

              {gpData.length > 0 && (
                <div
                  style={{
                    margin: "12px 16px",
                    padding: "10px 14px",
                    background: "#e6f7ff",
                    border: "1px solid #91d5ff",
                    borderRadius: 6,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    fontSize: 12,
                  }}
                >
                  <span><b>Grand Totals:</b></span>
                  <span>Families: <strong>{gpTotals.total_families.toLocaleString()}</strong></span>
                  <span>Members: <strong>{gpTotals.total_members.toLocaleString()}</strong></span>
                  <span>Income Verified: <strong>{gpTotals.income_verified.toLocaleString()}</strong></span>
                  <span>Not Verified: <strong>{gpTotals.income_not_verified.toLocaleString()}</strong></span>
                  <span>Pending: <strong>{gpTotals.income_pending.toLocaleString()}</strong></span>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* ==================== VILLAGE MODAL ==================== */}
        <Modal
          open={villageModalOpen}
          onCancel={() => setVillageModalOpen(false)}
          width={1400}
          destroyOnClose
          footer={null}
          closable
          style={{ top: 20 }}
          styles={{ body: { padding: "16px 0 0 0", overflow: "hidden" } }}
          title={
            <div style={{ paddingRight: 40 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "16px", fontWeight: "600", color: "#1f1f1f" }}>
                  {villageModalTitle}
                </span>
                <Space>
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={() => exportToPDF(villageData, villageTotals, "Village", "village")}
                    disabled={villageLoading || villageData.length === 0}
                  >
                    Export PDF
                  </Button>
                  <Button
                    type="primary"
                    icon={<FileExcelOutlined />}
                    onClick={() => exportToExcel(villageData, villageTotals, "Village", "village")}
                    disabled={villageLoading || villageData.length === 0}
                  >
                    Export Excel
                  </Button>
                </Space>
              </div>
            </div>
          }
        >
          {villageLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
              <p style={{ marginTop: 10, color: "#666" }}>Loading villages...</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", height: "calc(80vh - 110px)", overflow: "hidden" }}>
              <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
                <Table
                  columns={villageColumns}
                  dataSource={villageData}
                  bordered
                  size="small"
                  scroll={{ x: 1800 }}
                  pagination={{
                    current: villageCurrentPage,
                    pageSize: villagePageSize,
                    total: villageData.length,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} villages`,
                  }}
                  onChange={(p) => {
                    setVillageCurrentPage(p.current);
                    setVillagePageSize(p.pageSize);
                  }}
                />
              </div>

              {villageData.length > 0 && (
                <div
                  style={{
                    margin: "12px 16px",
                    padding: "10px 14px",
                    background: "#e6f7ff",
                    border: "1px solid #91d5ff",
                    borderRadius: 6,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    fontSize: 12,
                  }}
                >
                  <span><b>Grand Totals:</b></span>
                  <span>Families: <strong>{villageTotals.total_families.toLocaleString()}</strong></span>
                  <span>Members: <strong>{villageTotals.total_members.toLocaleString()}</strong></span>
                  <span>Income Verified: <strong>{villageTotals.income_verified.toLocaleString()}</strong></span>
                  <span>Not Verified: <strong>{villageTotals.income_not_verified.toLocaleString()}</strong></span>
                  <span>Pending: <strong>{villageTotals.income_pending.toLocaleString()}</strong></span>
                </div>
              )}
            </div>
          )}
        </Modal>



      </div>
    </div>
  );
};

export default IncomeVerificationReportCEO;
